import { debounce } from "utils";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import React, { useEffect, useMemo, useRef, useState } from "react";

const RangeSlider = ({ minPrice, maxPrice, setMinPrice, setMaxPrice }) => {
  const navigate = useNavigate();
  const methods = useFormContext();
  const sliderWidth = 281;
  const minLimit = 1500;
  const maxLimit = 10000;
  const step = 100;

  const getPositionFromValue = useMemo(
    () => (value) =>
      24 + ((value - minLimit) / (maxLimit - minLimit)) * sliderWidth,
    [minLimit, maxLimit, sliderWidth]
  );

  const getValueFromPosition = useMemo(
    () => (position) => {
      const rawValue =
        minLimit + ((position - 24) / sliderWidth) * (maxLimit - minLimit);
      return Math.round(rawValue / step) * step;
    },
    [minLimit, maxLimit, sliderWidth, step]
  );
  const [leftThumbPosition, setLeftThumbPosition] = useState(
    getPositionFromValue(minPrice)
  );
  const [rightThumbPosition, setRightThumbPosition] = useState(
    getPositionFromValue(maxPrice)
  );
  const [draggingThumb, setDraggingThumb] = useState(null);
  const lastMovedThumb = useRef(null);

  const debouncedSetMinPrice = useMemo(
    () =>
      debounce((value) => {
        setMinPrice(value);
        updateQueryParams(value, maxPrice);
      }, 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMinPrice]
  );

  const debouncedSetMaxPrice = useMemo(
    () =>
      debounce((value) => {
        setMaxPrice(value);
        updateQueryParams(minPrice, value);
      }, 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setMaxPrice]
  );

  const updateQueryParams = (minPriceValue, maxPriceValue) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("minPrice", minPriceValue);
    searchParams.set("maxPrice", maxPriceValue);

    navigate({ search: searchParams.toString() }, { replace: true });
  };

  useEffect(() => {
    setLeftThumbPosition(getPositionFromValue(minPrice));
    setRightThumbPosition(getPositionFromValue(maxPrice));
  }, [minPrice, maxPrice, getPositionFromValue]);

  const parsePrice = (value, defaultValue) =>
    parseInt(value?.replace(/,/g, "").replace(/\$/g, ""), 10) || defaultValue;

  const currentMinPrice = parsePrice(
    methods.watch("currentMinPrice"),
    minPrice
  );
  const currentMaxPrice = parsePrice(
    methods.watch("currentMaxPrice"),
    maxPrice
  );

  const handleThumbMove = (event, isMinThumb) => {
    event.preventDefault();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const boundingRect = event.target.closest("svg").getBoundingClientRect();
    const newPosition = Math.min(
      sliderWidth + 24,
      Math.max(24, clientX - boundingRect.left)
    );
    const newValue = getValueFromPosition(newPosition);
    const minGap = 1000;

    if (isMinThumb) {
      if (newValue < currentMaxPrice - minGap && newValue >= minLimit) {
        methods.setValue("currentMinPrice", `$${newValue.toLocaleString()}`);
        setLeftThumbPosition(newPosition);
        debouncedSetMinPrice(newValue);
        lastMovedThumb.current = "min";
      } else if (newValue >= currentMaxPrice - minGap) {
        const adjustedValue = currentMaxPrice - minGap;
        methods.setValue(
          "currentMinPrice",
          `$${adjustedValue.toLocaleString()}`
        );
        setLeftThumbPosition(getPositionFromValue(adjustedValue));
        debouncedSetMinPrice(adjustedValue);
      }
    } else {
      if (newValue > currentMinPrice + minGap && newValue <= maxLimit) {
        methods.setValue("currentMaxPrice", `$${newValue.toLocaleString()}`);
        setRightThumbPosition(newPosition);
        debouncedSetMaxPrice(newValue);
        lastMovedThumb.current = "max";
      } else if (newValue <= currentMinPrice + minGap) {
        const adjustedValue = currentMinPrice + minGap;
        methods.setValue(
          "currentMaxPrice",
          `$${adjustedValue.toLocaleString()}`
        );
        setRightThumbPosition(getPositionFromValue(adjustedValue));
        debouncedSetMaxPrice(adjustedValue);
      }
    }
  };

  const attachThumbEvents = (isMinThumb) => (event) => {
    event.preventDefault();
    setDraggingThumb(isMinThumb ? "left" : "right");
    const handleMove = (e) => handleThumbMove(e, isMinThumb);
    const handleEnd = () => {
      setDraggingThumb(null);
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleEnd);
  };

  return (
    <svg
      width="329"
      height="24"
      viewBox="0 0 329 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="24" y1="12" x2="305" y2="12" stroke="#B0B0B0" strokeWidth="2" />
      <line
        x1={leftThumbPosition}
        y1="12"
        x2={rightThumbPosition}
        y2="12"
        stroke="#2B364B"
        strokeWidth="2"
      />
      {lastMovedThumb.current === "max" ? (
        <>
          <circle
            cx={leftThumbPosition}
            cy="12"
            r="11"
            fill={draggingThumb === "left" ? "#2B364B" : "white"}
            stroke="#2B364B"
            strokeWidth="2"
            onMouseDown={attachThumbEvents(true)}
            onTouchStart={attachThumbEvents(true)}
            style={{ cursor: "pointer" }}
          />
          <circle
            cx={rightThumbPosition}
            cy="12"
            r="11"
            fill={draggingThumb === "right" ? "#2B364B" : "white"}
            stroke="#2B364B"
            strokeWidth="2"
            onMouseDown={attachThumbEvents(false)}
            onTouchStart={attachThumbEvents(false)}
            style={{ cursor: "pointer" }}
          />
        </>
      ) : (
        <>
          <circle
            cx={rightThumbPosition}
            cy="12"
            r="11"
            fill={draggingThumb === "right" ? "#2B364B" : "white"}
            stroke="#2B364B"
            strokeWidth="2"
            onMouseDown={attachThumbEvents(false)}
            onTouchStart={attachThumbEvents(false)}
            style={{ cursor: "pointer" }}
          />
          <circle
            cx={leftThumbPosition}
            cy="12"
            r="11"
            fill={draggingThumb === "left" ? "#2B364B" : "white"}
            stroke="#2B364B"
            strokeWidth="2"
            onMouseDown={attachThumbEvents(true)}
            onTouchStart={attachThumbEvents(true)}
            style={{ cursor: "pointer" }}
          />
        </>
      )}
    </svg>
  );
};

export default RangeSlider;
